import { useState, useEffect } from 'react';
import { api } from '@/App';
import { Zap } from 'lucide-react';

const HOUSE_TO_TUYA_OWNER = {
  'Saint Georges': '26773026',
  'Versailles': '179894974',
};

/**
 * Bandeau conso live — visible dans le header.
 * Affiche : conso totale Linky + top appareils mesurés Tuya + reste non-mesuré.
 * Cache backend 5 min (0 quota Tuya additionnel).
 */
const TopConsumersBar = ({ currentHouse, limit = 3 }) => {
  const [data, setData] = useState({ consumers: [], total_watts: 0, linky_total_w: null, other_watts: null });
  const [loading, setLoading] = useState(true);
  const houseOwner = currentHouse ? HOUSE_TO_TUYA_OWNER[currentHouse.name] : null;

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const params = new URLSearchParams({ limit: String(limit) });
        if (houseOwner) params.append('house_id', houseOwner);
        const { data: resp } = await api.get(`/integrations/tuya/top-consumers?${params}`);
        if (!cancelled && resp?.success) setData(resp);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [houseOwner, limit]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 text-[10px] font-mono text-cyan-500/50" data-testid="top-consumers-loading">
        <Zap className="w-3 h-3 animate-pulse" />
        Lecture conso...
      </div>
    );
  }

  const { consumers, total_watts, linky_total_w, other_watts } = data;
  const fmtW = (w) => w >= 1000 ? `${(w/1000).toFixed(2)}kW` : `${Math.round(w)}W`;

  return (
    <div
      className="flex items-center gap-2 px-2 py-0.5 text-[9px] font-mono bg-black/40 border border-yellow-500/20 rounded-full overflow-hidden"
      data-testid="top-consumers-bar"
    >
      {/* Total Linky temps réel — la vraie référence */}
      {linky_total_w !== null && (
        <span
          className="flex items-center gap-1 whitespace-nowrap pr-2 border-r border-yellow-500/20"
          title="Conso totale temps réel mesurée par votre Linky (Enedis)"
          data-testid="linky-total"
        >
          <Zap className="w-3 h-3 text-yellow-300" />
          <span className="text-gray-400">LINKY</span>
          <span className="text-yellow-300 font-bold">{fmtW(linky_total_w)}</span>
        </span>
      )}

      {/* Top mesurés */}
      <div className="flex items-center gap-3 overflow-x-auto no-scrollbar flex-1">
        {consumers.length === 0 ? (
          <span className="text-gray-500 whitespace-nowrap">aucun smart-plug actif</span>
        ) : (
          consumers.map((c, i) => (
            <span key={c.id} className="flex items-center gap-1 whitespace-nowrap" data-testid={`top-consumer-${i}`}>
              <span className="text-gray-500">{i + 1}.</span>
              <span className="text-white truncate max-w-[140px]">{c.name}</span>
              <span className="text-yellow-400 font-bold">{fmtW(c.watts)}</span>
            </span>
          ))
        )}
      </div>

      {/* Reste non-mesuré (= Linky - sum mesurés) */}
      {other_watts !== null && other_watts > 0 && (
        <span
          className="whitespace-nowrap pl-2 border-l border-yellow-500/20"
          title={`Conso non monitorée (lave-vaisselle, four, frigos, chauffe-eau Ariston, etc.) = ${Math.round(other_watts)}W`}
          data-testid="other-watts"
        >
          <span className="text-gray-500">+ AUTRES </span>
          <span className="text-orange-400 font-bold">{fmtW(other_watts)}</span>
        </span>
      )}

      {/* Si pas de Linky : montrer juste le total mesuré */}
      {linky_total_w === null && total_watts > 0 && (
        <span className="text-cyan-400 font-bold whitespace-nowrap">Σ {fmtW(total_watts)}</span>
      )}
    </div>
  );
};

export default TopConsumersBar;
